/**
 * Marketplace Analytics — per-basket detail page.
 *
 * Sections:
 *   A. Basket header (title / publisher / category / risk / status)
 *   B. Backtest stats (the marketed numbers from the listing row)
 *   C. Live aggregate KPIs + equity curve (sum of subscribers' basket pnl)
 *   D. Subscriber roster table — click a row → drawer with their trades
 *   E. Breakdowns (strategy / underlying / etc.)
 *
 * Backend gate (`/analytics/marketplace/baskets/{id}`) returns 403 when the
 * caller is neither the basket's publisher, an AccountManager managing at
 * least one of its subscribers, nor a SuperAdmin. AccountManager loads come
 * back with `scope='managed_users_only'`; the UI labels the aggregate /
 * roster sections accordingly so KPIs aren't misread as basket-wide totals.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, Eye, Home, LineChart as LineChartIcon,
  RefreshCw,
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';
import { useActingAs } from '../context/ActingAsContext';
import marketplaceAnalyticsService from '../services/marketplaceAnalyticsService';
import { marketplaceDefault30d } from '../utils/dateUtils';
import { formatInr, formatPct, pnlClass } from '../utils/platformFormat';
import { cn } from '../utils/cn';
import { exitTypeLabel } from '../utils/exitTypeLabel';
import type {
  DateRangePreset,
  DetailSection,
  MarketplaceDetailResponse,
  MarketplaceSubscriberRow,
} from '../types/marketplaceAnalytics';
import SubscriberDrilldownDrawer from '../components/marketplace/SubscriberDrilldownDrawer';
// Phase 5A/5B: new shell + Overview components.
import MarketplaceFreshnessBanner from '../components/marketplace/MarketplaceFreshnessBanner';
import MarketplaceDateRangeStrip from '../components/marketplace/MarketplaceDateRangeStrip';
import MarketplaceSidebarNav from '../components/marketplace/MarketplaceSidebarNav';
import MarketplaceKpiStrip from '../components/marketplace/MarketplaceKpiStrip';
import MarketplaceEquityChart from '../components/marketplace/MarketplaceEquityChart';
import MarketplaceDrawdownChart from '../components/marketplace/MarketplaceDrawdownChart';
import MarketplaceDailyPnlTable from '../components/marketplace/MarketplaceDailyPnlTable';

const VALID_SECTIONS: DetailSection[] = [
  'overview', 'strategies', 'trades', 'time', 'execution', 'download',
];
const VALID_PRESETS: DateRangePreset[] = [
  'today', '7d', '30d', '90d', 'fy', 'ytd', '1y', 'all', 'custom',
];


// T10 + U3: tone resolver for KPI tiles whose value can be null/undefined on
// legacy listings (missing `backtest_summary` fields). null/0 → neutral
// (gray), strictly positive → green, strictly negative → red.
//
// U3: 0 maps to 'neutral' (matching `pnlClass(0)` in utils/platformFormat.ts
// — a breakeven or no-trade value gets the SAME colour on the strategy table
// (which uses pnlClass) and on KPI tiles (which use pnlTone). Pre-fix:
// pnlTone(0)='pos' (green) vs pnlClass(0)=neutral (gray) — same value
// rendered two colours on one page.
const pnlTone = (v: number | null | undefined): 'pos' | 'neg' | 'neutral' => {
  if (v == null) return 'neutral';
  if (v > 0) return 'pos';
  if (v < 0) return 'neg';
  return 'neutral';
};

const MarketplaceAnalyticsDetailPage: React.FC = () => {
  const { basketId } = useParams<{ basketId: string }>();
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin, isAccountManager, isLoading: authLoading } = useAuth();
  const canViewAnalytics = isAdmin || isSuperAdmin || isAccountManager;
  const { setActingAs } = useActingAs();

  const [data, setData] = useState<MarketplaceDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drillSubscriber, setDrillSubscriber] = useState<MarketplaceSubscriberRow | null>(null);

  // Phase 5A: URL-driven section + date-range state. Persists across
  // refresh/share without an extra context. Defaults: 30d window + overview
  // section. Reading once on mount + writing back through `setSearchParams`
  // keeps a single source of truth.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDefaults = useMemo(() => marketplaceDefault30d(), []);
  const section = useMemo<DetailSection>(() => {
    const raw = searchParams.get('section');
    return (raw && (VALID_SECTIONS as string[]).includes(raw))
      ? (raw as DetailSection) : 'overview';
  }, [searchParams]);
  const preset = useMemo<DateRangePreset>(() => {
    const raw = searchParams.get('preset');
    return (raw && (VALID_PRESETS as string[]).includes(raw))
      ? (raw as DateRangePreset) : '30d';
  }, [searchParams]);
  const startParam = searchParams.get('start') || initialDefaults.start;
  const endParam = searchParams.get('end') || initialDefaults.end;

  // Ref-based cancellation token shared by useEffect + handleRefresh.
  // Each fetch bumps the token; only the latest fetch's resolution wins
  // (R9 — fixes stale Refresh response overwriting fresh data or warning
  // "state-update on unmounted component" when the user navigates away
  // mid-flight). Ref over state so bumping doesn't re-trigger the effect.
  const fetchTokenRef = React.useRef(0);

  // Stash the latest URL-derived fetch params on a ref so `loadDetail`'s
  // closure can read them at fetch time WITHOUT taking them as deps. This
  // avoids the cancellation-storm where `useSearchParams` returning a fresh
  // reference (or any other shallow churn) flips `loadDetail`'s identity,
  // re-fires the useEffect, bumps the token, and orphans the previous
  // fetch's setIsLoading(false) — locking the page in "Loading basket…".
  const fetchParamsRef = React.useRef<{
    start: string; end: string; preset: DateRangePreset;
    section: DetailSection; live: boolean | undefined;
  }>({
    start: startParam, end: endParam, preset, section,
    live: searchParams.get('live') === 'false' ? false : undefined,
  });
  React.useEffect(() => {
    fetchParamsRef.current = {
      start: startParam, end: endParam, preset, section,
      live: searchParams.get('live') === 'false' ? false : undefined,
    };
  }, [startParam, endParam, preset, section, searchParams]);

  // Shared loader: bumps the token, runs the fetch, and discards the
  // response if the token has moved on. The two callers (useEffect for
  // initial load, handleRefresh for the button) share this so any future
  // wiring change (e.g. AbortController hookup) lands in one place
  // instead of drifting between two near-identical copies.
  const loadDetail = React.useCallback((errPrefix: string) => {
    if (!basketId) return;
    fetchTokenRef.current += 1;
    const myToken = fetchTokenRef.current;
    setIsLoading(true);
    setError(null);
    marketplaceAnalyticsService.detail(basketId, fetchParamsRef.current)
      .then((resp) => {
        if (myToken === fetchTokenRef.current) setData(resp);
      })
      .catch((err) => {
        if (myToken === fetchTokenRef.current) {
          setError(err?.message || errPrefix);
        }
      })
      .finally(() => {
        if (myToken === fetchTokenRef.current) setIsLoading(false);
      });
  }, [basketId]);

  useEffect(() => {
    if (!basketId) return;
    // R6: gate backend fetch on auth — without this an unauthorized user
    // navigating directly to /admin/marketplace-analytics/:basketId
    // triggers the audit-row + DDB fan-out before the render-time Navigate
    // short-circuits. authLoading=true also waits for auth resolution
    // before issuing the fetch.
    if (authLoading) return;
    if (!canViewAnalytics) return;

    loadDetail('Failed to load basket');

    // S6: cleanup bumps the token so any resolution arriving AFTER
    // unmount or a basketId change sees a stale token and skips its
    // setState calls. Prevents "state-update on unmounted component"
    // warnings AND avoids stale-fetch overwrites when the user rapidly
    // navigates between baskets. The underlying HTTP request still
    // completes (we don't have an AbortController hooked up yet) but
    // its result is silently discarded.
    return () => {
      fetchTokenRef.current += 1;
    };
  }, [basketId, authLoading, canViewAnalytics, loadDetail]);

  const handleRefresh = React.useCallback(() => {
    if (isLoading) return;
    loadDetail('Failed to refresh basket');
  }, [isLoading, loadDetail]);

  const listing = data?.listing;
  const agg = data?.aggregate;
  const subs = data?.subscribers || [];
  // Default to 'full' when the backend omits scope (older responses /
  // SuperAdmin / publisher) so the UI degrades safely to the existing
  // basket-wide labels.
  const isManagedScope = data?.scope === 'managed_users_only';
  const aggregateTitle = isManagedScope
    ? 'Live across your managed users'
    : 'Live across subscribers';
  if (authLoading) return null;
  if (!canViewAnalytics) return <Navigate to="/" replace />;

  return (
    <StandardLayout>
      <PageHeader
        title={listing?.title || 'Basket'}
        pageType="management"
        icon={<LineChartIcon className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Marketplace Analytics', href: '/admin/marketplace-analytics' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/marketplace-analytics')}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to list
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              leftIcon={<RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>
        }
      />

      {/* Phase 5A: data freshness — today-live ✓ / stale / truncated banner.
          Sits below the page header so operators see freshness before
          reading any KPI. */}
      <MarketplaceFreshnessBanner aggregate={agg} />

      {/* Phase 5A: date-range strip — preset chips + custom pickers.
          Writes back to URL params so refresh / share round-trips. */}
      <MarketplaceDateRangeStrip
        preset={preset}
        start={startParam}
        end={endParam}
        onChange={(next) => {
          const sp = new URLSearchParams(searchParams);
          sp.set('preset', next.preset);
          sp.set('start', next.start);
          sp.set('end', next.end);
          setSearchParams(sp, { replace: true });
        }}
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading basket…</span>
        </div>
      )}

      {/* Phase 5A: sidebar + main content. The sidebar carries the 6 section
          chips; main content is gated on `section` so only one view renders
          at a time. Sidebar sits on top on mobile (md:grid-cols-[200px_1fr]
          stack). */}
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        <MarketplaceSidebarNav
          active={section}
          onChange={(next) => {
            const sp = new URLSearchParams(searchParams);
            sp.set('section', next);
            setSearchParams(sp, { replace: true });
          }}
          // V1 only Overview ships substantive content; Trades / Download
          // are placeholders pending Phase 5C-5D backends.
          disabledSections={[]}
        />
        <div className="space-y-4 min-w-0">

      {/* ─── Section A: Header ─── */}
      {listing && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle>{listing.title || '(untitled)'}</CardTitle>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Published by {listing.publisher_email || listing.publisher_user_id} ·{' '}
                  {listing.published_at ? `Published ${listing.published_at.slice(0, 10)}` : 'Not published'}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {listing.category && <Chip label={listing.category} tone="neutral" />}
                {listing.risk_level && <Chip label={listing.risk_level} tone="warning" />}
                {!listing.is_published && <Chip label="DRAFT" tone="neutral" />}
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* ─── OVERVIEW SECTION: KPI strip + Backtest tile + equity + drawdown
          + daily P&L. The 9-card KPI strip uses Phase 4A rollup-stamped
          KPIs when available (CAGR / Sharpe / Max DD / Profit Factor) AND
          falls back to legacy fields the rollup-less aggregate produces. ─── */}
      {section === 'overview' && agg && (
        <>
          <MarketplaceKpiStrip kpi={agg.kpi} />
        </>
      )}

      {/* Backtest tile — moved into Overview but rendered as a compact card
          so the marketed numbers stay glanceable next to the live KPIs. */}
      {section === 'overview' && listing && (
        <>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white pt-2">Backtest (marketed numbers)</h2>
          {/* Legacy listings can land here without a backtest_summary object
              (or with individual fields missing). Optional-chain every field
              read so a single malformed row can't crash the entire page. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi label="CAGR" value={formatPct(listing.backtest_summary?.cagr, 1)} tone={pnlTone(listing.backtest_summary?.cagr)} />
            <Kpi label="Total Return" value={formatPct(listing.backtest_summary?.total_return, 1)} tone="neutral" />
            <Kpi label="Win Rate" value={formatPct(listing.backtest_summary?.win_rate, 1)} tone="neutral" />
            <Kpi label="Sharpe" value={listing.backtest_summary?.sharpe_ratio != null ? listing.backtest_summary.sharpe_ratio.toFixed(2) : '—'} tone="neutral" />
            <Kpi label="Backtest P&L" value={formatInr(listing.backtest_summary?.net_pnl)} tone={pnlTone(listing.backtest_summary?.net_pnl)} />
            {/* Backend stores `max_drawdown` as an absolute peak-to-trough
                rupee amount (`backtest_stats.py`: `abs(min(equity - peak))`),
                NOT a ratio. Render as INR with a leading minus to match the
                Backtest P&L tile and the analytics-page `PerformanceReport`
                convention — formatPct here would print "59708.0%" for a
                ₹59,708 drawdown. Guard on `> 0` (not `!= null`) so a stored
                0 — which can come from a degraded-sentinel listing row that
                lost its backtest stats — renders as "—" rather than the
                misleading "-₹0.00". */}
            <Kpi label="Max Drawdown" value={(listing.backtest_summary?.max_drawdown ?? 0) > 0 ? `-${formatInr(listing.backtest_summary!.max_drawdown!)}` : '—'} tone="neg" />
          </div>
        </>
      )}

      {/* Live aggregate — Overview section. */}
      {section === 'overview' && agg && (
        <>
          <div className="flex items-center gap-2 pt-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{aggregateTitle}</h2>
            {isManagedScope && (
              // Scope chip — makes it visually unambiguous that KPIs below
              // are NOT basket-wide. Sits next to the section heading so the
              // reader can't miss it before glancing at the numbers.
              <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200 uppercase">
                Managed-users scope
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* U4: route through pnlTone so a true-zero net P&L (no trades /
                breakeven) renders neutral, consistent with the Backtest
                tiles above and the strategy table's pnlClass below. */}
            <Kpi label="Live Net P&L" value={formatInr(agg.kpi.net_pnl)} tone={pnlTone(agg.kpi.net_pnl)} />
            <Kpi label="Trades" value={String(agg.kpi.trades_total)} tone="neutral" />
            <Kpi label="Win Rate" value={formatPct(agg.kpi.win_rate, 1)} tone="neutral" />
            {/* V1: route through pnlTone so an all-losing window doesn't
                render a negative `best_day` in green (or vice versa) — same
                neutral-on-zero contract as the Live Net P&L tile above. */}
            <Kpi label="Best Day" value={formatInr(agg.kpi.best_day_pnl)} tone={pnlTone(agg.kpi.best_day_pnl)} />
            <Kpi label="Worst Day" value={formatInr(agg.kpi.worst_day_pnl)} tone={pnlTone(agg.kpi.worst_day_pnl)} />
            <Kpi label="Active Days" value={String(agg.kpi.active_days)} tone="neutral" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Cumulative live equity</CardTitle></CardHeader>
              <CardContent>
                {/* Phase 5B: extracted to a shared Recharts component so the
                    same shape renders identically across detail page + any
                    future basket-compare surface. */}
                <MarketplaceEquityChart equityCurve={agg.equity_curve || []} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Drawdown</CardTitle></CardHeader>
              <CardContent>
                {/* Phase 5B: client-derived from the equity curve so we
                    don't need a second backend call. */}
                <MarketplaceDrawdownChart
                  equityCurve={agg.equity_curve || []}
                  capitalDeployed={agg.kpi?.capital_deployed}
                />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Daily P&amp;L</CardTitle></CardHeader>
            <CardContent className="p-0">
              <MarketplaceDailyPnlTable equityCurve={agg.equity_curve || []} />
            </CardContent>
          </Card>
        </>
      )}

      {/* ─── Section D: Subscribers (Overview only) ─── */}
      {section === 'overview' && subs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {isManagedScope ? 'Your managed users' : 'Subscribers'} ({subs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Subscriber</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Capital</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Trades</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Win %</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Net P&L</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">ROI %</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Last trade</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {subs.map((s) => (
                    <tr key={s.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {s.full_name || '(unnamed)'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{s.email}</div>
                        <div className="mt-1 flex gap-1 flex-wrap">
                          {s.accounts.map((a) => (
                            <span key={`${a.broker}:${a.client_id}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 uppercase">
                              <span className={cn('inline-block h-1.5 w-1.5 rounded-full',
                                a.auto_login_enabled ? 'bg-green-500' : 'bg-gray-400')} />
                              {a.broker}:{a.client_id}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className={cn(
                          'inline-flex items-center px-1.5 py-0.5 font-medium rounded uppercase',
                          s.status === 'ACTIVE'
                            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
                        )}>{s.status || '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">{formatInr(s.capital_allocated)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                        {s.has_traded ? (
                          <>
                            <span className="font-medium">{s.trades_total}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                              ({s.trades_won}W/{s.trades_lost}L)
                            </span>
                          </>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                        {s.has_traded ? formatPct(s.win_rate, 1) : '—'}
                      </td>
                      <td className={cn('px-4 py-3 text-right tabular-nums', pnlClass(s.net_pnl))}>
                        {s.has_traded ? formatInr(s.net_pnl) : '—'}
                      </td>
                      <td className={cn('px-4 py-3 text-right tabular-nums', pnlClass(s.roi_pct ?? 0))}>
                        {formatPct(s.roi_pct)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-500 dark:text-gray-400 text-xs">
                        {s.last_trade_date || 'Never'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDrillSubscriber(s)}
                            disabled={!s.has_traded}
                            title={s.has_traded ? 'View trades' : 'No trades yet'}
                          >
                            View trades
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              // Pass the subscriber's human label so the
                              // header chip shows a name instead of a UUID
                              // while SuperAdmin is viewing-as an unmanaged
                              // user_id. Fallbacks mirror the table render.
                              setActingAs(
                                s.user_id,
                                s.full_name || s.email || s.user_id,
                              );
                              navigate('/others/analytics');
                            }}
                            leftIcon={<Eye className="h-3.5 w-3.5" />}
                            title="Open this subscriber's analytics"
                          >
                            View as
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Strategies SECTION ─── */}
      {section === 'strategies' && agg && Object.keys(agg.breakdowns.strategy).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Strategy attribution</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Strategy</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Trades</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Wins</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Losses</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Net P&L</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {Object.entries(agg.breakdowns.strategy)
                    .sort((a, b) => b[1].pnl - a[1].pnl)
                    .map(([sid, v]) => (
                      <tr key={sid} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3">
                          {/* T9: optional-chain the property too — a
                              response missing `strategy_lookup` entirely
                              (older cached payload / API drift) would
                              otherwise throw 'Cannot read properties of
                              undefined' and unmount the whole detail card. */}
                          <div className="text-gray-900 dark:text-white">{data?.strategy_lookup?.[sid] || sid}</div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{v.count}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-green-700 dark:text-green-300">{v.wins ?? 0}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-red-700 dark:text-red-300">{v.losses ?? 0}</td>
                        <td className={cn('px-4 py-3 text-right tabular-nums', pnlClass(v.pnl))}>{formatInr(v.pnl)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Time SECTION — hour / dow / dte breakdowns ─── */}
      {section === 'time' && agg && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(['hour', 'dow', 'dte'] as const).map((panel) => {
            const entries = Object.entries(
              (agg.breakdowns as any)?.[panel] || {},
            );
            return (
              <Card key={panel}>
                <CardHeader>
                  <CardTitle className="capitalize">
                    {panel === 'dow' ? 'Day of Week'
                      : panel === 'dte' ? 'Days to Expiry'
                      : 'Hour of Day'}
                    <span className="ml-2 text-xs font-normal text-amber-600">(approximate)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {entries.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No data in this window.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left uppercase tracking-wider text-gray-500 dark:text-gray-300">Bucket</th>
                            <th className="px-3 py-2 text-right uppercase tracking-wider text-gray-500 dark:text-gray-300">Trades</th>
                            <th className="px-3 py-2 text-right uppercase tracking-wider text-gray-500 dark:text-gray-300">Net P&amp;L</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {entries.sort((a, b) => (b[1] as any).pnl - (a[1] as any).pnl)
                            .map(([k, v]: [string, any]) => (
                              <tr key={k} className="bg-white dark:bg-gray-800">
                                <td className="px-3 py-2 font-mono">{k}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{v.count}</td>
                                <td className={cn('px-3 py-2 text-right tabular-nums', pnlClass(v.pnl))}>
                                  {formatInr(v.pnl)}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ─── Execution SECTION — exit-reason + underlying breakdowns ─── */}
      {section === 'execution' && agg && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(['exit_reason', 'underlying'] as const).map((panel) => {
            const entries = Object.entries(
              (agg.breakdowns as any)?.[panel] || {},
            );
            return (
              <Card key={panel}>
                <CardHeader>
                  <CardTitle className="capitalize">
                    {panel === 'exit_reason' ? 'Exit Reason' : 'Underlying'}
                    <span className="ml-2 text-xs font-normal text-amber-600">(approximate)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {entries.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No data in this window.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-300">{panel === 'exit_reason' ? 'Reason' : 'Symbol'}</th>
                            <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-gray-500 dark:text-gray-300">Trades</th>
                            <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-gray-500 dark:text-gray-300">Net P&amp;L</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {entries.sort((a, b) => (b[1] as any).pnl - (a[1] as any).pnl)
                            .map(([k, v]: [string, any]) => (
                              <tr key={k} className="bg-white dark:bg-gray-800">
                                <td className="px-4 py-2">{panel === 'exit_reason' ? exitTypeLabel(k) : k}</td>
                                <td className="px-4 py-2 text-right tabular-nums">{v.count}</td>
                                <td className={cn('px-4 py-2 text-right tabular-nums', pnlClass(v.pnl))}>
                                  {formatInr(v.pnl)}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ─── Trades / Download SECTIONS — pending Phase 5C / 5D backends ─── */}
      {section === 'trades' && (
        <Card>
          <CardHeader><CardTitle>Trades</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm text-gray-600 dark:text-gray-300 py-8 text-center">
              Trade-level drilldown is shipping in Phase 5C — pending the
              `/baskets/{'{id}'}/trades` paginated endpoint.
            </div>
          </CardContent>
        </Card>
      )}

      {section === 'download' && (
        <Card>
          <CardHeader><CardTitle>Download</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm text-gray-600 dark:text-gray-300 py-8 text-center">
              CSV export is shipping in Phase 5D — pending the
              `/baskets/{'{id}'}/download` endpoint + audit wiring.
            </div>
          </CardContent>
        </Card>
      )}

        </div>{/* end main content column */}
      </div>{/* end sidebar+content grid */}

      {drillSubscriber && basketId && (
        <SubscriberDrilldownDrawer
          basketId={basketId}
          subscriber={drillSubscriber}
          onClose={() => setDrillSubscriber(null)}
        />
      )}
    </StandardLayout>
  );
};

// ─── child components ────────────────────────────────────────────────────

const Kpi: React.FC<{ label: string; value: string; tone: 'pos' | 'neg' | 'neutral' }> = ({ label, value, tone }) => {
  const toneClass = tone === 'pos' ? 'text-green-600 dark:text-green-400'
    : tone === 'neg' ? 'text-red-600 dark:text-red-400'
    : 'text-gray-900 dark:text-white';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
      <div className={cn('text-xl font-bold mt-1 tabular-nums', toneClass)}>{value}</div>
    </div>
  );
};

const Chip: React.FC<{ label: string; tone: 'neutral' | 'warning' }> = ({ label, tone }) => (
  <span className={cn(
    'inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded uppercase',
    tone === 'warning'
      ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200',
  )}>
    {label}
  </span>
);


export default MarketplaceAnalyticsDetailPage;
